
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import AccountHeader from "@/components/account/AccountHeader";
import AccountTabs from "@/components/account/AccountTabs";
import SubscriptionForm from "@/components/subscription/SubscriptionForm";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";
import { useProfile } from "@/hooks/useProfile";

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const profileHook = useProfile();
  
  useEffect(() => {
    const checkUser = async () => {
      setIsLoadingProfile(true);
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          navigate("/verify");
          return;
        }
        setUserId(data.session.user.id);
        const profile = await profileHook.fetchProfile(data.session.user.id);
        if (profile) {
          setPhoneNumber(profile.phone_number);
          setIsAdmin(profile.role === 'admin');
          setProfileData(profile);
        }
      } catch (error) {
        console.error("Session check error:", error);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    checkUser();
  }, [navigate]);
  
  const {
    subscriptionData,
    isLoadingSubscription,
    isLoading,
    subscriptionError,
    updateSubscription,
    unsubscribe
  } = useSubscription(userId, phoneNumber);
  
  if (isLoadingProfile) {
    return (
      <div className="container py-4 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="space-y-8">
          <Skeleton className="h-12 w-full" />
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Skeleton className="h-6 w-3/4 mx-auto" />
                <Skeleton className="h-6 w-1/2 mx-auto" />
                <div className="space-y-6 mt-8">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  if (!userId) {
    return (
      <div className="container py-8 max-w-4xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Authentication error. Please <a href="/verify" className="underline">sign in</a> again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  if (subscriptionError) {
    return (
      <div className="container py-4 max-w-4xl mx-auto">
        <AccountHeader phoneNumber={phoneNumber || ""} />
        <AccountTabs activeTab="subscription" />
        <Alert variant="destructive" className="mt-8">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading subscription. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  const hasActiveSubscription = subscriptionData?.subscription?.status === "active";
  const title = hasActiveSubscription ? "Update Your Subscription" : "Create Your Subscription";
  const description = hasActiveSubscription ? "You have an active subscription. Update your preferences below." : "Set up your preferences to receive inspirational quotes.";
  
  return (
    <div className="container py-4 max-w-4xl mx-auto">
      <AccountHeader phoneNumber={phoneNumber || ""} />
      <AccountTabs activeTab="subscription" />
      
      <Card className="mt-8">
        <CardContent className="pt-6">
          <h2 className="text-2xl font-semibold text-center mb-2">{title}</h2>
          <p className="text-center text-gray-600 mb-6">{description}</p>
          
          {isLoadingSubscription ? (
            <div className="space-y-4 py-4">
              <Skeleton className="h-6 w-3/4 mx-auto" />
              <Skeleton className="h-6 w-1/2 mx-auto" />
              <div className="space-y-6 mt-8">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          ) : (
            <SubscriptionForm 
              initialCategories={subscriptionData?.categories || []}
              initialTimeSlots={subscriptionData?.timeSlots || [{ time: "09:00", categoryId: null }]}
              initialTimezone={subscriptionData?.subscription?.timezone || "Australia/Sydney"}
              isLoading={isLoading || isLoadingSubscription}
              hasActiveSubscription={hasActiveSubscription}
              onSubmit={updateSubscription.mutate}
              onUnsubscribe={unsubscribe}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
